import { Component, Input, OnChanges, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CardInputRequest, CardSubmitUseCase, EventBridge, EventData, EventType, IframeCreationRequest, InputType } from '@payments/ngpgcard-utils';
import { Subscription } from 'rxjs';

import { AppConstants } from '../../../config/appConstants';
import { BehaviorDataService } from '../../../service/behaviorData.service';
import { CardIframeService } from '../../../service/card-iframe-service';
import { DepositService } from '../../../service/deposit.service';
import { DepositRedirectService } from '../../../service/depositRedirect.service';
import { LoaderService } from '../../../service/loader.service';
import { UserDataService } from '../../../service/userDataService';

@Component({
    selector: 'app-cc-input',
    templateUrl: 'cc-input.component.html',
    styleUrls: ['./cc-input.component.scss'],
    imports: [TranslateModule],
})
export class CCInputComponent implements OnInit, OnDestroy, OnChanges {
    depositService = inject(DepositService);
    depositRedirectService = inject(DepositRedirectService);
    private behaviorData = inject(BehaviorDataService);
    private userDataService = inject(UserDataService);
    private translate = inject(TranslateService);
    loaderService = inject(LoaderService);
    private cardIframeService = inject(CardIframeService);

    @Input() ccInputFormGroup: FormGroup;
    isCvvDialogVisible = signal<boolean>(false);
    cvvMinLength: number;
    cvvMaxLength: number;
    $subscriptions: Subscription[] = [];
    iframeInitlialized: boolean = false;

    initializeParent() {
        if (this.cardIframeService._iframeService() && this.cardIframeService._eventBridge()) {
            const themeName: string = sessionStorage.getItem('currentTheme')?.replace('cashier-', '') || '';
            this.cardIframeService._eventBridge()?.receiveEvent(EventType.page_loaded, this.sendCardInputRequest.bind(this));
            const request: IframeCreationRequest = {
                ngpgHost: this.userDataService?.userData?.configParams?.pgw_frontend_url,
                themeName: themeName || '',
                usecase: CardSubmitUseCase.update_cvv,
                styleProperties: {
                    width: '100pct',
                    height: '60px',
                    border: '0',
                },
            };
            setTimeout(() => {
                const response = this.cardIframeService._iframeService()?.createPaymentIframe(request);
                if (response?.success) {
                    this.cardIframeService._eventBridge()?.initializeParent();
                    this.cardIframeService._eventBridge()?.receiveEvent(EventType.show_cvv_tooltip, this.showCVVTooltip.bind(this));
                    this.getCvv2Validation();
                }
            }, 0);
        }
    }
    getCvv2Validation() {
        this.setCvv2ClearValidations();
        this.cardIframeService._eventBridge()?.receiveEvent(EventType.card_form_valid, () => {
            this.ccInputFormGroup.setErrors(null);
        });
        this.cardIframeService._eventBridge()?.receiveEvent(EventType.card_form_invalid, () => {
            this.ccInputFormGroup.setErrors({ invalid: true });
        });
        this.$subscriptions.push(
            this.behaviorData.getCVV2.subscribe(() => {
                if (this.depositService.showDepositFailure()) {
                    this.setCvv2ClearValidations();
                }
            }),
        );
    }
    setCvv2ClearValidations() {
        this.cardIframeService._eventBridge()?.sendMessageToIframe({ eventName: EventType.card_input_enable });
        this.cardIframeService._eventBridge()?.sendMessageToIframe({ eventName: EventType.card_input_clear });
        this.ccInputFormGroup.setErrors({ invalid: true });
    }

    ngOnInit(): void {
        this.createCvvFormControl();
    }

    showCVVTooltip(): any {
        if (this.depositService?.depositInfo?.paymentMethod.toUpperCase() != AppConstants.AMEX) {
            this.isCvvDialogVisible.set(true);
        }
    }
    closetooltip() {
        this.isCvvDialogVisible.set(false);
    }

    private createCvvFormControl() {
        this.$subscriptions.push(
            this.behaviorData.getCVV2.subscribe(() => {
                if (this.depositService.showDepositFailure()) {
                    this.cardIframeService._eventBridge()?.sendMessageToIframe({ eventName: EventType.focus_input });
                }
            }),
        );
    }

    sendCardInputRequest(type: string = '') {
        const validationMessages = { [InputType.card_cvv]: { required: this.translate.instant('PARTY.CASHIER.web.deposit.cc.cvv.required3') } };
        const inputProperties = {
            [InputType.card_cvv]: {
                required: true,
                minLength: this.cvvMinLength,
                maxLength: this.cvvMaxLength,
                labelText: this.translate.instant('PARTY.CASHIER.web.deposit.cc.cvv.label'),
            },
        };
        const themeName: string = sessionStorage.getItem('currentTheme')?.replace('cashier-', '') || '';
        const { borderColor, textColor } = this.getInputFieldStyle();
        const request: CardInputRequest = {
            usecase: CardSubmitUseCase.update_cvv,
            paymentMethod: this.depositService?.depositInfo?.paymentMethod?.toUpperCase(),
            validationMessages,
            inputProperties,
            additionalData: {
                themeName,
                borderColor,
                textColor,
            },
            iframeSource: 'quickdeposit-app',
        };

        const eventData: EventData = { eventName: EventType.card_input, payload: request };
        eventData.eventName = type == 'update' ? EventType.update_card_input : EventType.card_input;
        this.cardIframeService._eventBridge()?.sendMessageToIframe(eventData);
    }

    ngOnChanges() {
        this.setCVVMinMaxLength();
        if (this.iframeInitlialized) {
            this.setCvv2ClearValidations();
            this.sendCardInputRequest('update');
        } else {
            this.iframeInitlialized = true;
            this.initializeParent();
        }
    }

    setCVVMinMaxLength() {
        if (this.depositService?.depositInfo) {
            const cvvLength = this.depositService?.depositInfo?.paymentMethod.toUpperCase() === AppConstants.AMEX ? 4 : 3;
            this.cvvMinLength = cvvLength;
            this.cvvMaxLength = cvvLength;
        }
    }

    getInputFieldStyle() {
        const matWrapperElement = document?.querySelector('.cashier-form-wrapper .form-control-wrapper .mat-mdc-text-field-wrapper');
        const matLabelElement = document?.querySelector('.mat-mdc-form-field mat-label');
        return {
            borderColor: (matWrapperElement && getComputedStyle(matWrapperElement)?.borderColor) || '#d5d5d5',
            textColor: (matLabelElement && getComputedStyle(matLabelElement)?.color) || '#333',
        };
    }

    ngOnDestroy() {
        this.iframeInitlialized = false;
        this.cardIframeService._eventBridge()?.sendMessageToIframe({ eventName: EventType.clear_events });
        this.cardIframeService._eventBridge()?.removeAll();
        this.cardIframeService.eventBridge = new EventBridge();
        this.$subscriptions.map((sub) => sub.unsubscribe());
    }
}
